import React from 'react';
import { UserPlus, MailCheck, Search, CreditCard, Coins, Unlock, IdCard, PhoneCall, ChevronRight, ChevronDown } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { WORKFLOW_STEPS, bi, TutorialStepId } from '@/data/tutorial';

const ICONS: Partial<Record<TutorialStepId, React.ElementType>> = {
  account: UserPlus,
  verify: MailCheck,
  browse: Search,
  buy: CreditCard,
  credits: Coins,
  unlock: Unlock,
  info: IdCard,
  contact: PhoneCall,
};

/**
 * Create Account → Verify Email → Browse Leads → Buy Lead or Membership →
 * Receive Credits → Unlock Lead → View Homeowner Information → Contact Customer
 *
 * Horizontal strip on desktop, vertical list on mobile. Labels come from the
 * shared tutorial data. `compact` drops the "where" hint for tight spaces.
 */
const WorkflowDiagram: React.FC<{ compact?: boolean; dark?: boolean; className?: string }> = ({ compact, dark, className = '' }) => {
  const { lang } = useI18n();
  const text = dark ? 'text-white' : 'text-slate-900';
  const sub = dark ? 'text-slate-300' : 'text-slate-500';

  return (
    <ol className={`grid gap-2 lg:grid-cols-8 lg:gap-0 ${className}`} aria-label={lang === 'es' ? 'Flujo de trabajo' : 'Workflow'}>
      {WORKFLOW_STEPS.map((s, idx) => {
        const Icon = ICONS[s.id] || Search;
        const last = idx === WORKFLOW_STEPS.length - 1;
        return (
          <li key={s.id} className="relative flex items-center gap-3 lg:flex-col lg:gap-2 lg:px-1 lg:text-center">
            <span
              className={`relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-sm ring-1 ${
                last ? 'bg-emerald-600 text-white ring-emerald-500' : idx === 3 ? 'bg-orange-500 text-white ring-orange-400' : dark ? 'bg-white/10 text-orange-300 ring-white/15' : 'bg-white text-orange-500 ring-slate-200'
              }`}
            >
              <Icon className="h-5 w-5" />
              <span className={`absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-black ${dark ? 'bg-white text-slate-900' : 'bg-slate-900 text-white'}`}>
                {idx + 1}
              </span>
            </span>
            <div className="min-w-0 flex-1 lg:flex-none">
              <p className={`text-sm font-extrabold leading-tight ${text}`}>{bi(s.label, lang)}</p>
              {!compact && <p className={`mt-0.5 text-[11px] leading-snug ${sub}`}>{bi(s.where, lang)}</p>}
            </div>
            {!last && (
              <>
                <ChevronRight className={`absolute -right-2 top-4 hidden h-5 w-5 lg:block ${dark ? 'text-white/30' : 'text-slate-300'}`} aria-hidden />
                <ChevronDown className={`absolute -bottom-2.5 left-4 h-4 w-4 lg:hidden ${dark ? 'text-white/30' : 'text-slate-300'}`} aria-hidden />
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default WorkflowDiagram;
